import { ChangeDetectionStrategy, Component, HostBinding, Input, OnInit } from '@angular/core';

import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { Observable, filter, map, startWith, switchMap } from 'rxjs';

import { CurrencyPipe } from '../../../base/pipes/currency.pipe';
import { UpsellEvent } from '../models';
import { UpsellPushService } from '../services/upsell-push.service';
import { UpsellWinningsService } from '../services/upsell-winnings.service';

interface ViewModel {
    currentWins: string;
    upsellWins: string;
    possibleWinningsText: string;
    strikeOutClass: string;
    isAccaBoostApplied: boolean;
}

@Component({
    selector: 'bs-upsell-winnings',
    templateUrl: './upsell-winnings.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class UpsellWinningsComponent implements OnInit {
    @HostBinding('class') className = 'betslip-upsell-winnings';
    @Input() event: UpsellEvent;

    vm$: Observable<ViewModel>;

    constructor(
        private sitecore: Sitecore,
        private winningsService: UpsellWinningsService,
        private currencyPipe: CurrencyPipe,
        private upsellPushService: UpsellPushService,
    ) {}

    ngOnInit(): void {
        this.vm$ = this.upsellPushService.upsellEvents$.pipe(startWith(this.event)).pipe(
            filter((upsellEvent) => this.event.bestSellingPick.id.isEqual(upsellEvent.bestSellingPick.id)),
            switchMap((upsellEvent) =>
                this.winningsService.getUpsellWinnings$(upsellEvent.bestSellingPick).pipe(
                    map(({ possibleWinnings, upsellPossibleWinnings, hasTaxation, hasAccaboost }) => {
                        const currentWins = this.currencyPipe.transform(possibleWinnings, true);
                        const upsellWins = this.currencyPipe.transform(upsellPossibleWinnings, true);

                        return {
                            currentWins,
                            upsellWins,
                            possibleWinningsText: this.getPossibleWinningsText(this.event, hasTaxation),
                            strikeOutClass: upsellWins.includes('--') ? '' : 'strike-out',
                            isAccaBoostApplied: !!hasAccaboost,
                        };
                    }),
                ),
            ),
        );
    }

    private getPossibleWinningsText(upsellEvent: UpsellEvent, withTaxation: boolean): string {
        if (!upsellEvent || !this.sitecore.betslipUpsell?.messages) {
            return '';
        }

        if (withTaxation && upsellEvent.isSelected) {
            return this.sitecore.betslipUpsell.messages.NewPossibleWinningsNetUpdated;
        } else if (upsellEvent.isSelected) {
            return this.sitecore.betslipUpsell.messages.NewPossibleWinningsUpdated;
        } else if (withTaxation) {
            return this.sitecore.betslipUpsell.messages.NewPossibleWinningsNet;
        }

        return this.sitecore.betslipUpsell.messages.NewPossibleWinnings;
    }
}
